import React, { useEffect, useState } from "react";
import {
  feedback_eye_centre_directly_above_shoulder_center,
  feedback_eye_horizontal,
  feedback_shoulders_horizontal,
  feedback_user_position_on_screen,
  throttleFunction,
} from "./utils";
import { Pose, POSE_CONNECTIONS } from "@mediapipe/pose";
import { drawConnectors, drawLandmarks } from "@mediapipe/drawing_utils";
import { Camera } from "@mediapipe/camera_utils";
import { Button, Modal } from "../../components";
import styles from "./Interview.module.css";
import {
  Check,
  Crosshair,
  CrossSvg,
  Maximize,
  Monitor,
  UserCheck,
} from "../../components/Icons/Icons";

const postureHeadings = {
  head_check_pass: "Head Position",
  screen_position_check_pass: "On Screen Position",
  // eye_check_pass: "Eye Contact",
  screen_distance_check_pass: "Screen Distance",
  shoulder_check_pass: "Shoulder Position",
};

const postureHeadingDescription = {
  head_check_pass: "So that your head in not tilted towards one side",
  screen_position_check_pass: "Being centered in the screen is first step towards maintaining eye contact",
  // eye_check_pass: "Eye Contact",
  screen_distance_check_pass: "No too close, not too far. Lean just right towards interviewer",
  shoulder_check_pass: "Being slouched can make you look disinterested. Straight posture reflects your confidence",
};

const postureIcons = {
  head_check_pass: { icon: <Crosshair />, color: "rgb(241,211,133)" },
  screen_position_check_pass: { icon: <Maximize />, color: "rgb(188,242,215)" },
  screen_distance_check_pass: { icon: <Monitor />, color: "rgb(183,210,247)" },
  shoulder_check_pass: { icon: <UserCheck />, color: "rgb(215,184,237)" },
};

const postureDescription = {
  head_check_pass_false:
    "Your head seems to be tilted on either side. Straighten your head",
  head_check_pass_true:
    "Your head is straight now. Try to maintain throughout the interview",
  screen_position_check_pass_false:
    "Try to be in the centre of the screen. Use center box as reference",
  screen_position_check_pass_true:
    "You're in positioned correctly in the center. Don't move during interview",
  eye_check_pass_false: "Look into the centre of the screen.",
  eye_check_pass_true: "Try to keep your eye contact throughout the interview",
  screen_distance_check_pass_false:
    "Move closer to the screen, to fit your entire face in the box.",
  screen_distance_check_pass_true:
    "Try to keep the same distance throughout the interview.",
  shoulder_check_pass_false: "Straighten up your shoulders",
  shoulder_check_pass_true:
    "Try to keep your shoulders straight throughout the interview.",
};

function Interview() {
  let videoElement, canvasElement, canvasCtx, timeout, cameraRef;
  const [postureResults, setPostureResults] = useState({});
  const [startTime, setStartTime] = useState(new Date());
  const [stopCalculation, toggleStopCalculation] = useState(false);
  const [showModal, toggleModal] = useState(true)

  useEffect(() => {
    document.title = 'Live Interview Posture Correction | BigResume';
  }, [])
  
  useEffect(() => {
    handlePosture();

    return () => closeCamera();
  }, []);

  //   useEffect(() => {
  //     if (!stopCalculation) {
  //       handlePause();
  //     }
  //     return () => clearTimeout(timeout);
  //   }, [stopCalculation]);

  const closeCamera = () => {
    cameraRef.stop();
    clearTimeout(timeout);
  };

  //   const handlePause = () => {
  //     timeout = setTimeout(() => {
  //       toggleStopCalculation(true);
  //       const videoElement = document.getElementsByClassName("input_video")[0];
  //       videoElement.pause();
  //     }, 60000);
  //   };

  const handlePosture = () => {
    videoElement = document.getElementsByClassName("input_video")[0];
    canvasElement = document.getElementsByClassName("output_canvas")[0];
    canvasCtx = canvasElement.getContext("2d");
    const pose = new Pose({
      locateFile: (file) => {
        console.log('file', file);
        return `https://cdn.jsdelivr.net/npm/@mediapipe/pose/${file}`;
      },
    });

    pose.setOptions({
      modelComplexity: 1,
      smoothLandmarks: true,
      enableSegmentation: true,
      smoothSegmentation: true,
      minDetectionConfidence: 0.5,
      minTrackingConfidence: 0.5,
    });

    pose.onResults(handlePoseResults);

    const camera = new Camera(videoElement, {
      onFrame: throttleFunction(async () => {
        await pose.send({ image: videoElement });
      }, 200),
      width: 590,
      height: 355,
    });
    cameraRef = camera;

    camera.start();
  };

  function handlePoseResults(results) {
    console.log('results', results);
    if (!results.poseLandmarks) {
      return;
    }

    canvasCtx.save();
    canvasCtx.clearRect(0, 0, canvasElement.width, canvasElement.height);
    canvasCtx.drawImage(
      results.segmentationMask,
      0,
      0,
      canvasElement.width,
      canvasElement.height
    );

    // Only overwrite existing pixels.
    canvasCtx.globalCompositeOperation = "source-in";
    canvasCtx.fillStyle = "#00000000"; // transparent fill
    canvasCtx.fillRect(0, 0, canvasElement.width, canvasElement.height);

    // Only overwrite missing pixels.
    canvasCtx.globalCompositeOperation = "destination-atop";
    canvasCtx.drawImage(
      results.image,
      0,
      0,
      canvasElement.width,
      canvasElement.height
    );

    canvasCtx.globalCompositeOperation = "source-over";
    drawConnectors(canvasCtx, results.poseLandmarks, POSE_CONNECTIONS, {
      color: "#00FF00",
      lineWidth: 1,
    });
    drawLandmarks(canvasCtx, results.poseLandmarks, {
      color: "#FF0000",
      lineWidth: 0.02,
    });

    canvasCtx.restore();
    getPoseFeedback(results);
  }

  const recaliberate = () => {
    const videoElement = document.getElementsByClassName("input_video")[0];
    videoElement.play();
    toggleStopCalculation(false);
    clearTimeout(timeout);
  };

  const getPoseFeedback = (results) => {
    let frame_landmarks = [];
    const poseLandmarks = [...results.poseLandmarks];
    const poseFeedback = {};

    // Invert y coordinates of each landmark
    for (var i = 0; i < poseLandmarks.length; i++) {
      let poseLandmarksTemp = { ...poseLandmarks[i] };
      poseLandmarksTemp["y"] = 1 - poseLandmarksTemp["y"];
      frame_landmarks.push(poseLandmarksTemp);
    }

    let user_position_on_screen =
      feedback_user_position_on_screen(frame_landmarks);

    const head_check =
      feedback_eye_centre_directly_above_shoulder_center(frame_landmarks);
    const shoulder_check = feedback_shoulders_horizontal(frame_landmarks);
    const eye_check = feedback_eye_horizontal(frame_landmarks);

    poseFeedback["screen_distance_check_pass"] =
      user_position_on_screen?.[0]?.screen_distance_check_pass;
    poseFeedback["screen_position_check_pass"] =
      user_position_on_screen?.[1]?.screen_position_check_pass;
    poseFeedback["head_check_pass"] = head_check?.[1];
    poseFeedback["shoulder_check_pass"] = shoulder_check?.[1];
    // poseFeedback["eye_check_pass"] = eye_check?.[1];

    setPostureResults(poseFeedback);
  };

  return (
    <>
    <Modal active={showModal}>
        <div className={styles.modal}>
          <h1 className={styles.modalTitle}>Welcome to Interview Posture Correction</h1>
          <p>
            Our AI powered technology helps you live correct your body language which
            results in improved posture for online interviews.
          </p>
          <div style={{marginTop: 24}}>
            {Object.keys(postureHeadings).map((item) => (

              <div className={`${styles.modal_posture_title}`}>
                <div
                  style={{ background: postureIcons[item]["color"] }}
                  className={styles.postureIcon}
                >
                  {postureIcons[item]["icon"]}
                </div>
                <div style={{display:'inline-block', width: '75%'}}>
                {postureHeadings[item]}
                <p>{postureHeadingDescription[item]}</p>
                </div>

              </div>
            ))}
          </div>
          <p>Follow on-screen instructions to correct all four paramteres</p>
          <Button content="Understood! Lets start" onClick={() => toggleModal(false)}/>
        </div>
      </Modal>
      <div textAlign="center" className={styles.interview}>
        {stopCalculation && (
          <div className="overlay">
            Your screen was idle for more than 1 minute,
            <br />
            Please recaliberate to check your real-time position.
            <Button
              onClick={recaliberate}
              style={{ background: "white" }}
              className="clr-green-500 fw-500 mt-7"
            >
              Recaliberate <i className="fas fa-redo ml-3"></i>
            </Button>
          </div>
        )}

        <p className={styles.semiTile}>
          Position your face inside the box and level your shoulders against the
          horizontal line{" "}
        </p>
        <div className={styles.video_wrapper}>
          <video className="input_video"></video>
          <div className={styles.output_canvas_wrapper}>
            <canvas
              width={"590"}
              height={"355"}
              className="output_canvas"
            ></canvas>
          </div>
        </div>
        <div className={`${styles.feedback_wrapper}`}>
          {Object.keys(postureHeadings).map((item) => (
            <div
              className={`${styles.pose_feedback} ${
                postureResults[item] ? styles.active : ""
              }`}
            >
              <div
                style={{ background: postureIcons[item]["color"] }}
                className={styles.postureIcon}
              >
                {postureIcons[item]["icon"]}
              </div>
              <p className={`${styles.feedback_title}`}>
                {postureResults[item] ? (
                  <Check style={{ color: "rgb(41, 192, 94)" }} />
                ) : (
                  <CrossSvg style={{ color: "rgb(245, 87, 103)" }} />
                )}{" "}
                {postureHeadings[item]}
              </p>
              <p className={styles.feedback_description}>
                {postureDescription[`${item}_${postureResults[item]}`]}
              </p>
            </div>
          ))}
        </div>
        {/* <video
          class="jw-video jw-reset"
          tabindex="-1"
          disableremoteplayback=""
          webkit-playsinline=""
          playsinline=""
          src="blob:https://speedostream.nl/0a2a8cf5-a376-4bde-b97f-817d4a991287"
        ></video> */}
      </div>
    </>
  );
}

export default Interview;
